// Маска растворения у краёв горизонтального скролл-трека.
//
// Зачем. Горизонтальные полосы хрома (вкладки `SegmentedControl`, строка
// контекста репетитора) прячут системный скроллбар — иначе он рисуется прямо
// внутри полосы. Но вместе со скроллбаром исчезает и ЕДИНСТВЕННЫЙ признак, что
// за краем есть ещё содержимое: человек видит два с половиной чипа и считает,
// что третьего нет. Градиент-маска у края — этот признак.
//
// Общий хук, а не копия на каждую полосу: логика (ResizeObserver на скроллер И
// на трек, RTL-нормализация `scrollLeft`, отсутствие маски у влезшего трека)
// маленькая, но целиком состоит из неочевидных углов.

import { type CSSProperties, useEffect, useState } from "react";

/** Есть ли что скроллить за левым / правым краем трека. */
export interface EdgeFade {
	left: boolean;
	right: boolean;
}

const NO_FADE: EdgeFade = { left: false, right: false };

/** Ширина растворения у края. Заметно, но не съедает соседний ярлык/чип. */
const FADE_WIDTH = "1.25rem";

/**
 * Значение `mask-image`.
 *
 * Цвет здесь — не цвет: в маске значащ только альфа-канал (`#000` =
 * непрозрачно, `transparent` = вырезано), поэтому семантические токены темы тут
 * неприменимы и подсказка одинаково работает в обеих темах. Значение
 * динамическое (три комбинации краёв), отсюда inline-style, а не класс.
 */
function edgeMask(fade: EdgeFade): string | undefined {
	const solid = `#000 ${FADE_WIDTH}`;
	const tail = `#000 calc(100% - ${FADE_WIDTH}), transparent 100%`;
	if (fade.left && fade.right) return `linear-gradient(to right, transparent 0, ${solid}, ${tail})`;
	if (fade.right) return `linear-gradient(to right, ${tail})`;
	if (fade.left) return `linear-gradient(to right, transparent 0, ${solid})`;
	return undefined;
}

export interface EdgeFadeMask {
	/**
	 * Вешается на САМ скроллер (элемент с `overflow-x: auto`).
	 *
	 * Именно callback-ref, а не `RefObject`. С объектом-ссылкой эффект видел
	 * `ref.current === null` у любого потребителя с ранним `return null` (строка
	 * контекста репетитора гаснет, пока чипов нет) и молча выходил; `enabled` при
	 * этом не менялся, перезапустить эффект было нечем — и появившиеся позже чипы
	 * оставались без маски навсегда. Callback-ref делает монтирование узла
	 * зависимостью эффекта.
	 */
	ref: (node: HTMLDivElement | null) => void;
	/** `style` скроллера; `undefined` — трек влез целиком, затемнять нечего. */
	style: CSSProperties | undefined;
}

/**
 * Слежение за переполнением одного скроллера. Вынесено из хука отдельной
 * функцией — здесь вся неочевидная механика, и её надо проверять тестом, а не
 * глазами.
 *
 * @returns функция отписки.
 */
export function observeEdgeFade(node: HTMLElement, onFade: (fade: EdgeFade) => void): () => void {
	const update = (): void => {
		const max = node.scrollWidth - node.clientWidth;
		// В RTL `scrollLeft` отрицателен (0 — у правого края), а маска рисуется
		// в ФИЗИЧЕСКИХ координатах: приводим позицию к расстоянию от левого края,
		// иначе в RTL подсказка встала бы не с той стороны.
		const fromLeft = node.scrollLeft < 0 ? max + node.scrollLeft : node.scrollLeft;
		onFade(max <= 1 ? NO_FADE : { left: fromLeft > 1, right: fromLeft < max - 1 });
	};

	// Переполнение меняется и без скролла: смена локали (ярлыки длиннее),
	// перетянутая ширина панели, появившийся чип. Наблюдаем и сам скроллер, и
	// его содержимое — первый ловит изменение доступной ширины, второе контента.
	const resize = new ResizeObserver(update);
	const observeAll = (): void => {
		resize.disconnect();
		resize.observe(node);
		for (const child of node.children) resize.observe(child);
		update();
	};
	observeAll();
	node.addEventListener("scroll", update, { passive: true });

	// Наблюдение за размерами детей раздаётся ОДИН раз, а состав полосы живой:
	// чипы источников прикрепляются и снимаются по ходу разговора. Сами чипы —
	// `shrink-0`, то есть добавление пятого не меняет размеров первых четырёх и
	// ResizeObserver молчит: без наблюдения за СОСТАВОМ маска не пересчиталась бы
	// ровно в тот момент, ради которого написана.
	const mutation =
		typeof MutationObserver === "undefined" ? null : new MutationObserver(observeAll);
	mutation?.observe(node, { childList: true });

	return () => {
		node.removeEventListener("scroll", update);
		resize.disconnect();
		mutation?.disconnect();
	};
}

/**
 * @param enabled Полоса в скролл-режиме. Бывает динамическим
 * (`enabled={isMobile}`) — при выключении маска сбрасывается, иначе посчитанная
 * в скролл-режиме залипла бы на первый кадр возврата.
 */
export function useEdgeFadeMask(enabled = true): EdgeFadeMask {
	const [node, setNode] = useState<HTMLDivElement | null>(null);
	const [fade, setFade] = useState<EdgeFade>(NO_FADE);

	useEffect(() => {
		// `NO_FADE` — стабильная ссылка, лишнего ре-рендера сброс не даёт.
		if (!enabled || !node || typeof ResizeObserver === "undefined") {
			setFade(NO_FADE);
			return undefined;
		}
		return observeEdgeFade(node, (next) =>
			setFade((prev) => (prev.left === next.left && prev.right === next.right ? prev : next)),
		);
	}, [enabled, node]);

	const mask = edgeMask(fade);
	return { ref: setNode, style: mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined };
}
